import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, BookOpen, ChevronRight, Search } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { HELP_ARTICLES, HELP_CATEGORY_ORDER, HelpArticle } from '../../../../help/articles';

const MarkdownArticle = React.lazy(() => import('./MarkdownArticle'));

interface HelpCenterProps {
  onBack?: () => void;
}

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('nb-NO', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const Spinner: React.FC = () => (
  <div className="flex items-center justify-center py-16">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

const ArticleView: React.FC<{ article: HelpArticle; onBack: () => void }> = ({ article, onBack }) => {
  const { t } = useTranslation();
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setMarkdown(null);
    setError(false);
    fetch(article.file)
      .then(r => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then(text => { if (!cancelled) setMarkdown(text); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [article.file]);

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('manager.modules.helpCenter.allArticles', {}, 'Alle artikler')}
      </button>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <p className="text-xs font-medium text-blue-600 mb-1">{article.category}</p>
        <h1 className="text-xl font-semibold text-gray-900 mb-6">{article.title}</h1>

        {error ? (
          <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
            {t('manager.modules.helpCenter.loadError', {}, 'Kunne ikke laste artikkelen.')}
          </div>
        ) : markdown === null ? (
          <Spinner />
        ) : (
          <Suspense fallback={<Spinner />}>
            <MarkdownArticle markdown={markdown} />
          </Suspense>
        )}

        <p className="mt-8 pt-4 border-t border-gray-100 text-xs text-gray-500">
          {t('manager.modules.helpCenter.validity', { '0': article.validFrom, '1': formatDate(article.lastVerified) },
            'Gjelder fra v{0} · sist kontrollert {1}')}
        </p>
      </div>
    </div>
  );
};

export const HelpCenter: React.FC<HelpCenterProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  // ?module=help-center&article=<id> åpner artikkelen direkte (lenker fra andre moduler).
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    const id = new URLSearchParams(window.location.search).get('article');
    return id && HELP_ARTICLES.some(a => a.id === id) ? id : null;
  });

  const selected = HELP_ARTICLES.find(a => a.id === selectedId) ?? null;

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = HELP_ARTICLES.filter(a =>
      !q || a.title.toLowerCase().includes(q) || a.summary.toLowerCase().includes(q));
    return HELP_CATEGORY_ORDER
      .map(category => ({ category, articles: matches.filter(a => a.category === category) }))
      .filter(g => g.articles.length > 0);
  }, [query]);

  if (selected) {
    return <ArticleView article={selected} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.buttons.back', {}, 'Tilbake')}
          </button>
        )}
        <h1 className="text-xl font-semibold text-gray-900">
          {t('manager.modules.helpCenter.title', {}, 'Hjelp og veiledning')}
        </h1>
      </div>

      <p className="text-sm text-gray-600">
        {t('manager.modules.helpCenter.intro', {}, 'Oppsett og forutsetninger for funksjoner i Compass.')}
      </p>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={t('manager.modules.helpCenter.search', {}, 'Søk i artiklene')}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {grouped.length === 0 ? (
        <p className="text-sm text-gray-500">
          {t('manager.modules.helpCenter.noMatch', {}, 'Ingen artikler matcher søket.')}
        </p>
      ) : (
        grouped.map(group => (
          <section key={group.category}>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">{group.category}</h2>
            <div className="space-y-2">
              {group.articles.map(article => (
                <button
                  key={article.id}
                  onClick={() => setSelectedId(article.id)}
                  className="w-full flex items-center gap-3 text-left bg-white rounded-lg border border-gray-200 px-4 py-3 hover:border-blue-300 hover:shadow-sm transition-all"
                >
                  <BookOpen className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{article.title}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{article.summary}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
};

export default HelpCenter;
